import React from 'react';
import { motion } from 'framer-motion';
import { Calculator, Users, Zap, Clock, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import { useCryptoPrices } from '../../hooks/useCryptoPrices';
import { formatCurrency } from '../../utils/currencyUtils';

export function StatsRow() {
  const { prices, isLoading, lastUpdate, refresh } = useCryptoPrices();
  const [nextUpdate, setNextUpdate] = React.useState(10000); // 10 seconds

  // Update countdown timer
  React.useEffect(() => {
    const timer = setInterval(() => {
      setNextUpdate(prev => {
        if (prev <= 1000) {
          refresh(true); // Force refresh when timer hits 0
          return 10000; // Reset to 10 seconds
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [refresh]);

  const getBTCPrice = () => {
    if (isLoading || !prices || !prices.BTC) {
      return (
        <div className="flex flex-col items-end">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse mt-1" />
        </div>
      );
    }

    const priceChange = prices.BTC.change24h || 0;
    const isPriceUp = priceChange >= 0;

    return (
      <div className="flex flex-col items-end">
        <div className="text-2xl font-bold text-gray-900">
          {formatCurrency(prices.BTC.price || 0, 'USD', { USD: 1 })}
        </div>
        <div className={`flex items-center gap-1 text-sm ${isPriceUp ? 'text-green-600' : 'text-red-600'}`}>
          {isPriceUp ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <TrendingDown className="w-4 h-4" />
          )}
          {Math.abs(priceChange).toFixed(2)}%
        </div>
      </div>
    );
  };

  const stats = [
    { 
      icon: Calculator, 
      label: 'BTC Price', 
      value: getBTCPrice(),
      update: `${Math.ceil(nextUpdate / 1000)}s`
    },
    { 
      icon: Users, 
      label: 'Active Users', 
      value: '25K+'
    },
    { 
      icon: Zap, 
      label: 'Network Hashrate', 
      value: isLoading ? (
        <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
      ) : (
        `${((prices?.BTC?.networkHashrate || 0) / 1e6).toFixed(1)} EH/s`
      )
    },
    { 
      icon: Clock, 
      label: 'Block Time', 
      value: isLoading ? (
        <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
      ) : (
        `${(prices?.BTC?.blockTime || 600) / 60} min`
      )
    }
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 + 0.8 }}
          className="group bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-50 group-hover:scale-110 transition-transform duration-300">
              <stat.icon className="w-6 h-6 text-blue-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                {typeof stat.value === 'string' ? (
                  <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                ) : (
                  stat.value
                )}
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm text-gray-500">{stat.label}</span>
                {stat.update && (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <button 
                      onClick={() => refresh(true)}
                      className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </button>
                    Updates in {stat.update}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}